import type { PlanId } from './pricing'

// Special cell tokens — rendered as styled states instead of plain text.
export const INCLUDED = '__included__'
export const NOT_AVAILABLE = '__not_available__'
export const CONTACT = '__contact__'

export type CostRow = {
  label: string
  detail?: string
  values: Record<PlanId, string>
}

export type CostSection = {
  id: string
  title: string
  rows: CostRow[]
  note?: string
  footnote?: string
  group?: { title: string; rows: CostRow[] }
  quoted?: { items: string[]; line: string }
}

const same = (value: string): Record<PlanId, string> => ({ launch: value, pilot: value, copilot: value, autopilot: value })
const tiered = (launch: string, pilot: string, copilot: string, autopilot: string): Record<PlanId, string> => ({
  launch,
  pilot,
  copilot,
  autopilot,
})

export const costSections: CostSection[] = [
  {
    id: 'included',
    title: 'Included Every Month',
    note: 'Usage beyond your allowance bills automatically at your package’s rates below.',
    rows: [
      {
        label: 'Included Usage Allowance',
        detail: 'Covers calls, texts, WhatsApp and AI; resets monthly, does not roll over',
        values: tiered('$5', '$15', '$30', '$60'),
      },
      {
        label: 'Team Seats Included',
        values: tiered('3', '5', '10', '20'),
      },
      {
        label: 'Your Phone, Text & Email Rates vs. Pilot',
        values: tiered('Same As Pilot', 'Standard', 'About 7% Lower', 'About 17% Lower'),
      },
    ],
  },
  {
    id: 'phone-text',
    title: 'Phone & Text',
    footnote:
      'No long-distance charges anywhere in the US and Canada. Calls to other countries bill at that country’s carrier rate.',
    rows: [
      { label: 'Local Phone Number', detail: 'Per number, per month', values: same('$3.45') },
      { label: 'Toll-Free Number', detail: 'Per number, per month', values: same('$6.45') },
      { label: 'Outbound Calls', detail: 'Per minute', values: tiered('4.2¢', '4.2¢', '3.92¢', '3.5¢') },
      { label: 'Inbound Calls', detail: 'Per minute', values: tiered('2.55¢', '2.55¢', '2.38¢', '2.13¢') },
      { label: 'Text Messages', detail: 'Per message segment', values: tiered('2.49¢', '2.49¢', '2.32¢', '2.08¢') },
      { label: 'AI Phone Answering', detail: 'Per minute', values: tiered('6¢', '6¢', '5.6¢', '5¢') },
      { label: 'WhatsApp', detail: 'Per message', values: tiered('22.2¢', '22.2¢', '20.72¢', '18.5¢') },
    ],
  },
  {
    id: 'email-automation',
    title: 'Email & Automation',
    rows: [
      { label: 'Email Sends', detail: 'Per 1,000 emails', values: tiered('$2.03', '$2.03', '$1.89', '$1.69') },
      { label: 'Premium Automation Steps', detail: 'Per run', values: tiered('3¢', '3¢', '2.8¢', '2.5¢') },
    ],
  },
  {
    id: 'ai',
    title: 'AI',
    rows: [
      { label: 'AI Writing', detail: 'Per 1,000 words', values: tiered('18¢', '18¢', '25.2¢', '22.5¢') },
      { label: 'AI Images', detail: 'Per image', values: tiered('12¢', '12¢', '16.8¢', '15¢') },
      { label: 'AI Review Replies', detail: 'Per review', values: tiered('3¢', '3¢', '2.8¢', '2.5¢') },
      { label: 'AI Page Builder', detail: 'Per page', values: tiered('$2.97', '$2.97', '$2.77', '$2.48') },
    ],
    group: {
      title: 'Advanced AI Tools',
      rows: [
        { label: 'AI Assistant & AI Studio', detail: 'Per 1M tokens', values: tiered('$6.33', '$6.33', '$5.91', '$5.28') },
        { label: 'AI Agents', detail: 'Per 750K tokens', values: tiered('75¢', '75¢', '70¢', '63¢') },
        { label: 'External AI — Input', detail: 'Per 750K words', values: tiered('$1.20', '$1.20', '$1.68', '$1.50') },
        { label: 'External AI — Output', detail: 'Per 750K words', values: tiered('$4.80', '$4.80', '$6.72', '$6.00') },
      ],
    },
  },
  {
    id: 'website',
    title: 'Website Add-Ons',
    rows: [
      {
        label: 'Extra Website Or Landing Page — Standard Hosting',
        detail: 'Per site, per month',
        values: tiered(CONTACT, '$30 ($25 at 4+ sites)', '$30 ($25 at 4+ sites)', '$30 ($25 at 4+ sites)'),
      },
      {
        label: 'Extra Website Or Landing Page — Advanced Hosting',
        detail: 'Per site, per month',
        values: tiered(CONTACT, '$97', '$97', '$97'),
      },
      { label: 'WordPress Hosting', detail: 'Per month', values: tiered(CONTACT, '$20', '$20', '$20') },
      {
        label: 'Extra Website Credits',
        detail: 'After 5-10 free overage credits',
        values: tiered(CONTACT, '$40 per 25', '$40 per 25', '$40 per 25'),
      },
      { label: 'Domain Purchase', detail: 'Per domain', values: same('$20') },
    ],
  },
  {
    id: 'marketing',
    title: 'Marketing Add-Ons',
    rows: [
      { label: 'Directory Listings, 200+ Sites', detail: 'Per month', values: tiered(CONTACT, '$79', INCLUDED, INCLUDED) },
      {
        label: 'Google Business Profile Posting, 2x/Month',
        detail: 'Per month',
        values: tiered(CONTACT, '$149', INCLUDED, INCLUDED),
      },
      {
        label: 'Click Fraud Protection',
        detail: 'Per month',
        values: tiered(NOT_AVAILABLE, NOT_AVAILABLE, '$99', '$99'),
      },
    ],
  },
  {
    id: 'quoted',
    title: 'Quoted Separately',
    rows: [],
    quoted: {
      items: [
        'Site Rescues',
        'Website Rebuilds Outside Your Package',
        'Custom Automation Builds',
        'Voice Clone Setup',
      ],
      line: 'We’ll always quote these before any work starts.',
    },
  },
]
